import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, X, PenLine, Sparkles, ClipboardCheck, FolderKanban } from "lucide-react";
import { api } from "@/lib/api";
import { HeroDemo } from "@/components/marketing/HeroDemo";
import { PricingCards } from "@/pages/marketing/Pricing";
import { Seo, RiskBadge } from "@/components/brand";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const H2 = ({ eyebrow, title, text, center }) => (
  <div className={center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
    <p className="eyebrow">{eyebrow}</p>
    <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">{title}</h2>
    {text && <p className="mt-4 text-base text-slate-400 md:text-lg">{text}</p>}
  </div>
);

const TONES = { green: "text-health bg-health/10", amber: "text-[#FFA000] bg-[#FFA000]/10", red: "text-alert bg-alert/10", navy: "text-slate-200 bg-white/10" };

const STEPS = [
  [PenLine, "Describe", "Type the job in plain English, or upload a scope of works."],
  [Sparkles, "Generate", "Saflr asks only what it needs, then drafts a structured document."],
  [ClipboardCheck, "Review", "Edit by hand or instruct the AI. Assumptions and gaps are flagged."],
  [FolderKanban, "Manage", "Approve, issue, track acknowledgements and schedule reviews."],
];

const FEATURES = [
  ["Team & workforce", "Invite your team with granular roles — owners, HSE managers, authors, reviewers, approvers, employees, contractors and auditors.", "Available", "/images/construction.jpg"],
  ["Approvals & digital sign-off", "Every AI draft starts as Draft. Move documents through Under Review → Approved → Issued with typed signatures and a complete audit trail.", "Available", null],
  ["Risk & compliance oversight", "Transparent indicators show what's in date, what's due for review and what's overdue — never a blanket declaration of compliance.", "Available", null],
  ["Training & competency", "AI-assisted course drafting, quizzes with pass marks, completion records, refresher reminders and a visual training matrix.", "Available", "/images/tablet.jpg"],
];

const INDUSTRIES = ["Construction", "Facilities Management", "Property Maintenance", "Property Management", "Manufacturing", "Warehousing", "Logistics", "Retail", "Hospitality", "Healthcare", "Education", "Cleaning", "Utilities", "Engineering", "Landscaping", "Offices & SMEs"];

const COMPARE = [
  ["Time to first draft", "Hours copying old templates", "Minutes from a short description"],
  ["Site-specific content", "Generic, often out of date", "Tailored to the activity and your company profile"],
  ["Missing information", "Easily overlooked", "Flagged as [TO BE CONFIRMED]"],
  ["Version control", "Files scattered across inboxes", "Controlled versions and audit history"],
  ["Approvals", "Printed, signed, scanned", "Digital review and sign-off workflow"],
  ["Review dates", "Spreadsheet reminders", "Automatic review reminders"],
];

const DEMO_ROWS = [["Fall from scissor lift platform", 4, 5, 2, 5], ["Collision with forklift traffic", 3, 4, 1, 4], ["Contact with live conductors", 3, 5, 1, 5]];

const Hero = ({ c }) => (
  <section className="relative overflow-hidden grid-bg noise">
    <div className="pointer-events-none absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-health/20 blur-[140px]" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-24 pt-16 lg:grid-cols-[1.1fr_1fr] lg:px-8 lg:pt-24">
      <div className="fade-up">
        <p className="eyebrow">Safety. Simplified.</p>
        <h1 data-testid="hero-heading" className="mt-5 text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl">{c.hero_heading}</h1>
        <p className="mt-6 max-w-xl text-base text-slate-400 md:text-lg">{c.hero_subheading}</p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/register" data-testid="hero-start-creating" className="group inline-flex items-center gap-2 rounded-full bg-health px-6 py-3.5 font-semibold text-navy transition-transform hover:-translate-y-0.5">{c.primary_cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
          <a href="#how" data-testid="hero-explore" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 font-semibold text-white transition-colors hover:bg-white/5">{c.secondary_cta}</a>
        </div>
        <p className="mt-6 font-mono text-xs text-slate-500">Describe the job. Let Saflr do the paperwork.</p>
      </div>
      <HeroDemo />
    </div>
  </section>
);

const Positioning = () => (
  <section className="border-y border-white/10 bg-navy-2/60">
    <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
      <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Drafts reference a reviewed library of UK legislation &amp; HSE guidance, including</p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {["HSWA 1974", "MHSWR 1999", "CDM 2015", "COSHH 2002", "Work at Height 2005", "PUWER 1998", "LOLER 1998", "RIDDOR 2013", "EAWR 1989", "RRO 2005"].map((t) => <span key={t} className="rounded-full border border-white/10 px-3 py-1 font-mono text-xs text-slate-300">{t}</span>)}
      </div>
    </div>
  </section>
);

const Categories = ({ cats }) => (
  <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
    <H2 eyebrow="Document library" title="Fourteen categories. Hundreds of document types." text="From RAMS to permits, COSHH to CDM — or describe something entirely bespoke." />
    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cats.map((c, i) => {
        const Icon = Icons[c.icon] || Icons.FileText;
        return (
          <div key={c.slug} data-testid={`category-card-${c.slug}`} className={`card-lift rounded-2xl border border-white/10 bg-white/[0.02] p-6 ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}>
            <div className={`grid h-10 w-10 place-items-center rounded-xl ${TONES[c.color] || TONES.navy}`}><Icon className="h-5 w-5" /></div>
            <h3 className="mt-5 text-lg font-semibold text-white">{c.name}</h3>
            <p className="mt-1 text-sm text-slate-400">{c.description}</p>
            <p className="mt-4 font-mono text-[11px] text-slate-500">{c.subcategories.length} document types</p>
            {i === 0 && <div className="mt-6 flex flex-wrap gap-1.5">{c.subcategories.slice(0, 12).map((s) => <span key={s} className="rounded-md bg-white/5 px-2 py-1 text-xs text-slate-300">{s}</span>)}</div>}
          </div>
        );
      })}
    </div>
  </section>
);

const How = () => (
  <section id="how" className="border-y border-white/10 bg-navy-2/50">
    <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
      <H2 eyebrow="How Saflr works" title="Describe. Generate. Review. Manage." />
      <div className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 md:grid-cols-4">
        {STEPS.map(([Icon, t, d], i) => (
          <div key={t} className="bg-navy p-8">
            <p className="font-mono text-xs text-health">0{i + 1}</p>
            <Icon className="mt-6 h-6 w-6 text-white" />
            <h3 className="mt-4 text-xl font-semibold text-white">{t}</h3>
            <p className="mt-2 text-sm text-slate-400">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Showcase = () => (
  <section className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 lg:grid-cols-2 lg:px-8">
    <div>
      <H2 eyebrow="AI document engine" title="Structured documents, not generic AI prose." text="Saflr produces real risk tables with initial and residual ratings, sequential methods with hold points, PPE, competence, emergency arrangements and sign-off — then flags every assumption it made." />
      <ul className="mt-8 space-y-3 text-sm text-slate-300">
        {["Quick Draft or Guided Professional modes", "Targeted AI edits: “Add controls for occupied premises”", "Only cites from a reviewed UK reference library", "Every document begins in Draft — never auto-approved"].map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 text-health" />{t}</li>)}
      </ul>
    </div>
    <div className="relative">
      <img src="/images/warehouse.jpg" alt="Warehouse with high-level lighting" className="h-[420px] w-full rounded-3xl object-cover opacity-60" />
      <div className="absolute inset-x-6 bottom-6 rounded-2xl border border-white/10 bg-navy/90 p-5 backdrop-blur-xl">
        <p className="eyebrow mb-3">Risk assessment · illustrative</p>
        <div className="space-y-2">
          {DEMO_ROWS.map(([h, l, s, rl, rs]) => (
            <div key={h} className="flex items-center justify-between gap-3 text-sm"><span className="truncate text-slate-200">{h}</span><span className="flex shrink-0 gap-2"><RiskBadge score={l * s} /><Icons.ArrowRight className="h-4 w-4 text-slate-500" /><RiskBadge score={rl * rs} /></span></div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section className="border-y border-white/10 bg-navy-2/50">
    <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
      <H2 eyebrow="One platform" title="Your entire HSE function, organised." />
      <div className="mt-12 grid gap-5 md:grid-cols-2">
        {FEATURES.map(([t, d, status, img]) => (
          <div key={t} className="card-lift overflow-hidden rounded-3xl border border-white/10 bg-navy">
            {img && <img src={img} alt={t} className="h-48 w-full object-cover opacity-70" />}
            <div className="p-7">
              <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${status === "Available" ? "bg-health/10 text-health" : "bg-[#FFA000]/10 text-[#FFA000]"}`}>{status}</span>
              <h3 className="mt-4 text-xl font-semibold text-white">{t}</h3>
              <p className="mt-2 text-sm text-slate-400">{d}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
    <H2 eyebrow="Industry solutions" title="Configured for the way your sector works." text="Choose one or more sectors during onboarding and Saflr tailors its drafts — without assuming every requirement applies to every business." />
    <div className="mt-10 flex flex-wrap gap-2.5">{INDUSTRIES.map((i) => <span key={i} className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-200 transition-colors hover:border-health/50">{i}</span>)}</div>
  </section>
);

const Compare = () => (
  <section className="border-y border-white/10 bg-navy-2/50">
    <div className="mx-auto max-w-5xl px-5 py-24 lg:px-8">
      <H2 center eyebrow="Saflr vs manual admin" title="Less paperwork. More oversight." />
      <div className="mt-12 overflow-hidden rounded-3xl border border-white/10">
        <div className="grid grid-cols-3 bg-white/5 px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400"><span /><span>Manual / Word templates</span><span className="text-health">Saflr</span></div>
        {COMPARE.map(([a, b, c]) => (
          <div key={a} className="grid grid-cols-3 gap-4 border-t border-white/10 px-6 py-4 text-sm">
            <span className="font-medium text-white">{a}</span>
            <span className="flex gap-2 text-slate-400"><X className="h-4 w-4 shrink-0 text-alert" />{b}</span>
            <span className="flex gap-2 text-slate-200"><Check className="h-4 w-4 shrink-0 text-health" />{c}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Faq = ({ faqs }) => (
  <section className="mx-auto max-w-3xl px-5 py-24 lg:px-8">
    <H2 center eyebrow="FAQ" title="Questions, answered." />
    <Accordion type="single" collapsible className="mt-10">
      {faqs.map((f, i) => (
        <AccordionItem key={i} value={`f${i}`} className="border-white/10">
          <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-white hover:no-underline">{f.q}</AccordionTrigger>
          <AccordionContent className="text-slate-400">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export default function Home() {
  const [page, setPage] = useState(null);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/cms/home").then((r) => setPage(r.data));
    api.get("/public/categories").then((r) => setCats(r.data));
  }, []);
  const c = page?.content || { hero_heading: "Health & Safety. Sorted in Seconds.", hero_subheading: "", primary_cta: "Start Creating", secondary_cta: "Explore Saflr", faqs: [] };
  return (
    <main>
      <Seo title={page?.seo_title} description={page?.seo_description} />
      {c.announcement && <div className="bg-health py-2 text-center text-sm font-medium text-navy" data-testid="announcement-banner">{c.announcement}</div>}
      <Hero c={c} />
      <Positioning />
      <Categories cats={cats} />
      <How />
      <Showcase />
      <Features />
      <Industries />
      <Compare />
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" id="pricing">
        <H2 center eyebrow="Pricing" title="Simple plans. Credits only charged on success." />
        <div className="mt-12"><PricingCards /></div>
      </section>
      <section className="border-y border-white/10 bg-navy-2/50">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center lg:px-8">
          <p className="eyebrow">Customer stories</p>
          <p className="mt-3 text-slate-400" data-testid="reviews-placeholder">Customer reviews and case studies will appear here once Saflr customers share them. We never publish invented testimonials.</p>
        </div>
      </section>
      <Faq faqs={c.faqs || []} />
      <section className="px-5 pb-24 lg:px-8">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-health p-10 text-navy md:p-16">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/20 blur-3xl" />
          <h2 className="relative max-w-2xl text-3xl font-bold sm:text-5xl">From a few words to a safer workplace.</h2>
          <p className="relative mt-4 max-w-xl text-navy/70">Create your first RAMS in minutes. Free trial, no card required.</p>
          <Link to="/register" data-testid="final-cta" className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 font-semibold text-white transition-transform hover:-translate-y-0.5">Start Creating <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </main>
  );
}
